/**
 * Müzakere kütüphanesi (web) saf kuralları. Mobil utils/library/*
 * (accessLock, ownerLibrary, review, diffPresentation, stepRules) ve
 * services/library.service.ts biçim indirgemeleriyle aynı.
 * Metinler çeviri anahtarı olarak döner; sayfalar t() ile yazar.
 */

import { LIMITS, newLocalQid, questionsIssue } from "../muzakere/questionRules";

/* ---------- Liste: sekme, sıralama, süzgeç (adres parametreleri) ---------- */

// Kütüphane kendi paketleriyle açılır (mobil gibi); keşif ve yıldızlılar yanında.
// "pending" (onay bekleyenler) yalnız Terakki yöneticisinde; yönetici webde
// /sistem/kutuphane kullanır, bu yüzden burada yok.
export const TABS = [
  { value: "mine", param: "paketlerim", key: "lib_tab_mine" },
  { value: "explore", param: "kesfet", key: "lib_tab_explore" },
  { value: "starred", param: "yildizlilar", key: "lib_tab_starred" },
];

export const SORTS = [
  { value: "stars", param: "yildiz", key: "lib_sort_stars" },
  { value: "new", param: "yeni", key: "lib_sort_new" },
  { value: "forks", param: "catal", key: "lib_sort_forks" },
];

export const DEFAULT_TAB = "mine";
export const DEFAULT_SORT = "stars";

const byParam = (list, param, fallback) => (list.find((x) => x.param === param) || { value: fallback }).value;
const paramOf = (list, value) => (list.find((x) => x.value === value) || {}).param;

/**
 * Adres parametrelerinden liste durumu: ?sekme=kesfet&sira=yeni&tavsiye=1&ara=...
 * Bilinmeyen değer varsayılana düşer; tavsiye süzgeci yalnız Keşfet'te.
 */
export function readListParams(params) {
  const get = (k) => (params && typeof params.get === "function" ? params.get(k) : null);
  const tab = byParam(TABS, get("sekme"), DEFAULT_TAB);
  const sort = byParam(SORTS, get("sira"), DEFAULT_SORT);
  const recommended = tab === "explore" && get("tavsiye") === "1";
  const q = String(get("ara") || "").slice(0, 60);
  return { tab, sort, recommended, q };
}

/** Liste durumunu adres parametrelerine yazar; varsayılanlar yazılmaz. */
export function writeListParams({ tab, sort, recommended, q }) {
  const out = {};
  if (tab && tab !== DEFAULT_TAB && paramOf(TABS, tab)) out.sekme = paramOf(TABS, tab);
  if (sort && sort !== DEFAULT_SORT && paramOf(SORTS, sort)) out.sira = paramOf(SORTS, sort);
  if (recommended && tab === "explore") out.tavsiye = "1";
  const query = String(q || "").trim();
  if (query) out.ara = query;
  return out;
}

/** /list gövdesi (mobil libraryService.list ile aynı). */
export function listBody({ tab, q, sort, page, recommended }) {
  const body = { tab };
  if (recommended && tab === "explore") body.recommended = true;
  const query = String(q || "").trim();
  if (query) body.q = query;
  if (sort) body.sort = sort;
  if (page && page > 1) body.page = page;
  return body;
}

/** Boş liste metni (sekmeye göre). */
export function emptyKey(tab) {
  if (tab === "mine") return "lib_empty_mine";
  if (tab === "starred") return "lib_empty_starred";
  return "lib_empty_explore";
}

/* ---------- Biçim indirgemeleri ---------- */

const idOf = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value.id ?? value._id ?? null;
};

const personName = (p) => (p && typeof p === "object" ? `${p.name || ""} ${p.surname || ""}`.trim() : "");

const requestStatus = (value) =>
  value === "approved" || value === "rejected" || value === "stale" ? value : "pending";

/** Kilitli paket özetinde bakan kişinin isteği tek biçime indirgenir. */
export function normalizePackage(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const req = raw.accessRequest ?? raw.myRequest;
  if (req === undefined) return raw;
  const { myRequest, ...rest } = raw;
  return {
    ...rest,
    accessRequest:
      req && typeof req === "object"
        ? { id: String(req.id ?? req._id ?? ""), status: requestStatus(req.status), message: req.message ?? "" }
        : null,
  };
}

/** Öneri özeti (sunucu packageId/packageTitle verir; eski adlar da kabul). */
export function normalizeProposal(raw) {
  const r = raw || {};
  const fork = r.fork && typeof r.fork === "object" ? r.fork : null;
  const target = r.target && typeof r.target === "object" ? r.target : null;
  return {
    id: String(r.id ?? r._id ?? ""),
    forkId: r.forkId ?? idOf(r.fork),
    targetId: r.targetId ?? r.packageId ?? idOf(r.target),
    forkTitle: r.forkTitle ?? fork?.title ?? r.snapshot?.title ?? "",
    targetTitle: r.targetTitle ?? r.packageTitle ?? target?.title ?? "",
    proposerName: r.proposerName ?? (personName(r.proposer) || ""),
    message: r.message ?? "",
    status: r.status ?? "open",
    reason: r.reason ?? "",
    createdAt: r.createdAt ?? null,
    decidedAt: r.decidedAt ?? null,
    canDecide: r.canDecide === true,
    isMine: r.isMine === true,
    locked: r.locked === true,
  };
}

/** İzin isteği: gömülü nesneler (package, requester, group) ya da düz adlar. */
export function normalizeAccessRequest(raw) {
  const r = raw || {};
  const pkg = r.package && typeof r.package === "object" ? r.package : null;
  const group = r.group && typeof r.group === "object" ? r.group : null;
  return {
    id: String(r.id ?? r._id ?? ""),
    packageId: r.packageId ?? idOf(r.package),
    packageTitle: r.packageTitle ?? pkg?.title ?? "",
    requesterName: r.requesterName ?? (personName(r.requester) || ""),
    groupName: r.groupName ?? group?.name ?? "",
    reason: r.reason === "A" || r.reason === "B" ? r.reason : null,
    status: requestStatus(r.status),
    message: r.message ?? "",
    requesterHasCopy: !!r.requesterHasCopy,
    canDecide: r.canDecide === true,
    createdAt: r.createdAt ?? null,
    decidedAt: r.decidedAt ?? null,
  };
}

/* ---------- Kilit ve izin istekleri ---------- */

/** İzin ve öneri kararındaki cevabın üst sınırı (sunucu da aynı). */
export const REPLY_MAX = 300;
/** Öneri mesajının üst sınırı. */
export const PROPOSAL_MESSAGE_MAX = 500;

const REASON_KEY = { A: "mzq_lock_reason_a", B: "mzq_lock_reason_b" };

/**
 * Kilitli paket satırının durumu. Yalnız sunucunun verdiği locked ve
 * accessRequest alanlarına bakar; istemci kilit kararı vermez.
 */
export function lockCardState(pkg) {
  if (!pkg || !pkg.locked) return { locked: false };
  const reasonKey = REASON_KEY[pkg.lockReason] || "mzq_lock_reason";
  const req = pkg.accessRequest;
  if (req?.status === "pending") {
    return { locked: true, status: "pending", canRequest: false, buttonKey: "mzq_access_pending", reasonKey, reply: null };
  }
  if (req?.status === "rejected") {
    const reply = String(req.message || "").trim();
    return { locked: true, status: "rejected", canRequest: true, buttonKey: "mzq_request_access", reasonKey, reply: reply || null };
  }
  if (req?.status === "stale") {
    return { locked: true, status: "stale", canRequest: true, buttonKey: "mzq_request_access", reasonKey, reply: null };
  }
  // Onaylanmış ama sunucu hâlâ kilitli diyorsa izin düşmüştür: yeniden istenir.
  return { locked: true, status: "none", canRequest: true, buttonKey: "mzq_request_access", reasonKey, reply: null };
}

/** İstek gönderilince satır sunucuyu beklemeden "İzin bekleniyor" olur. */
export const withPendingRequest = (pkg, requestId) => ({
  ...pkg,
  accessRequest: { id: requestId || "", status: "pending", message: "" },
});

/**
 * Sunucu kilitli özette yalnız bekleyen isteği verir; ret cevabı giden
 * isteklerden (yeniden eskiye) alınır.
 */
export function withLatestDecision(pkg, outgoing) {
  if (!pkg?.locked || pkg.accessRequest?.status === "pending") return pkg;
  const latest = (outgoing || []).find((r) => r.packageId === pkg.id);
  if (!latest || (latest.status !== "rejected" && latest.status !== "stale")) return pkg;
  return { ...pkg, accessRequest: { id: latest.id, status: latest.status, message: latest.message || "" } };
}

const STATUS_KEY = {
  pending: "lib_req_pending",
  approved: "lib_req_approved",
  rejected: "lib_req_rejected",
  stale: "lib_req_stale",
};

export const requestStatusKey = (status) => STATUS_KEY[status] || STATUS_KEY.pending;

/** Gelen istekteki uyarı: "{ad}, {grup} grubunuzun üyesi". */
export function requesterWarning(req, t) {
  const group = String(req.groupName || "").trim();
  return `${req.requesterName}, ${group ? `${group} ` : ""}${t("lib_group_member_suffix")}`;
}

/** İstek satırı alt bilgisi: durum ve varsa cevap. */
export function requestMeta(req, t) {
  const reply = String(req.message || "").trim();
  return [t(requestStatusKey(req.status)), reply ? `${t("lib_reply")}: ${reply}` : null].filter(Boolean).join(" · ");
}

export const pendingIncomingCount = (list) => (list || []).filter((r) => r.status === "pending").length;

/** Karar cevabı: kırpılır, boşsa hiç gönderilmez. */
export function replyPayload(text, max = REPLY_MAX) {
  const value = String(text || "").trim().slice(0, max);
  return value || undefined;
}

/* ---------- Sahip görünümü ---------- */

/** Paketlerim özet satırı: "3 paket · 7 yıldız · 2 çatal · 1 açık öneri". */
export function summaryLine(summary, t) {
  if (!summary) return null;
  return [
    `${summary.packages ?? 0} ${t("lib_word_package")}`,
    `${summary.stars ?? 0} ${t("lib_word_star")}`,
    `${summary.forks ?? 0} ${t("lib_word_fork")}`,
    `${summary.openProposals ?? 0} ${t("lib_word_open_proposal")}`,
  ].join(" · ");
}

export const openProposalLabel = (count, t) => (count > 0 ? `${count} ${t("lib_word_open_proposal")}` : null);

const time = (value) => (value ? Date.parse(value) || 0 : 0);

/** Öneriler: açıklar önce, sonra yeniden eskiye. */
export function sortProposals(list) {
  return [...(list || [])].sort((a, b) => {
    const open = Number(b.status === "open") - Number(a.status === "open");
    return open !== 0 ? open : time(b.createdAt) - time(a.createdAt);
  });
}

/** GG.AA.YYYY (yerel saat). */
export function formatDay(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Paket satırı alt bilgisi; Paketlerim'de çatal sayısı sıfır da yazılır. */
export function packageMeta(p, tab, t) {
  return [
    p.isMine ? null : p.ownerName,
    `${p.questionCount ?? 0} ${t("lib_word_question")}`,
    `${p.starCount ?? 0} ${t("lib_word_star")}`,
    p.forkCount || tab === "mine" ? `${p.forkCount ?? 0} ${t("lib_word_fork")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Çatal satırı: sahibi, çatal tarihi, yıldız ve soru sayısı. */
export function forkMeta(f, t) {
  return [
    f.ownerName || null,
    formatDay(f.forkedAt ?? f.createdAt) || null,
    `${f.starCount ?? 0} ${t("lib_word_star")}`,
    `${f.questionCount ?? 0} ${t("lib_word_question")}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Uygulanma satırı: "3 grupta uygulandı · 42 kişi cevapladı". Hiç
 * uygulanmamışsa null; always verilirse "Henüz hiçbir grupta uygulanmadı".
 */
export function usageLine(usage, t, { always = false } = {}) {
  if (!usage) return null;
  if (!usage.groupCount) return always ? t("lib_usage_none") : null;
  return `${usage.groupCount} ${t("lib_usage_groups")} · ${usage.participantCount ?? 0} ${t("lib_usage_people")}`;
}

/**
 * Terakki onayı rozeti: tavsiye onayı kapsar; kendi onaysız paketinde "Özel"
 * ya da "Onay bekliyor". Listede (showVerified false) onay yalnız simgeyle.
 */
export function reviewBadge(p, { showVerified = true } = {}) {
  if (p.recommended) return "lib_badge_recommended";
  if (p.verified) return showVerified ? "lib_badge_verified" : null;
  if (p.isMine && p.verified === false) return p.reviewState === "private" ? "lib_badge_private" : "lib_badge_pending";
  return null;
}

/** Sahibin yayın düğmesi: özel → onaya gönder, bekliyor → geri çek, onaylı → yayından kaldır. */
export function ownerReviewAction(pkg) {
  if (!pkg?.isMine || !pkg.reviewState) return null;
  if (pkg.reviewState === "private") return "submit";
  if (pkg.reviewState === "pending") return "withdraw";
  return "unpublish";
}

/* ---------- Öneri farkı ---------- */

const asList = (value) => (Array.isArray(value) ? value : []);
const str = (value) => (typeof value === "string" ? value : "");

function metaField(raw) {
  if (!raw || typeof raw !== "object") return undefined;
  return {
    current: str(raw.current ?? raw.from ?? raw.before),
    proposed: str(raw.proposal ?? raw.proposed ?? raw.to ?? raw.after),
  };
}

function tagsField(raw) {
  if (!raw || typeof raw !== "object") return undefined;
  const pick = (v) => (Array.isArray(v) ? v.map(String) : []);
  return {
    current: pick(raw.current ?? raw.from ?? raw.before),
    proposed: pick(raw.proposal ?? raw.proposed ?? raw.to ?? raw.after),
  };
}

/**
 * Sunucu farkını (libraryDiff.js) sayfanın biçimine indirger. changed[].qid
 * asıl paketin soru kimliğidir (kabulde choices anahtarı).
 */
export function normalizeDiff(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const changed = asList(src.changed)
    .map((item) => {
      const current = item?.current ?? item?.before ?? item?.original;
      const proposed = item?.proposed ?? item?.after ?? item?.proposal;
      if (!current || !proposed) return null;
      return { qid: String(item.qid ?? current.qid), current, proposed, conflict: item.conflict === true };
    })
    .filter(Boolean);
  const meta = {};
  const m = src.meta && typeof src.meta === "object" ? src.meta : {};
  const title = metaField(m.title);
  const description = metaField(m.description);
  const tags = tagsField(m.tags);
  if (title) meta.title = title;
  if (description) meta.description = description;
  if (tags) meta.tags = tags;
  return { added: asList(src.added), removed: asList(src.removed), changed, meta };
}

export function diffCounts(diff) {
  return {
    added: diff.added.length,
    removed: diff.removed.length,
    changed: diff.changed.length,
    conflicts: diff.changed.filter((c) => c.conflict).length,
    meta: Object.keys(diff.meta).length,
  };
}

/** "2 eklenen · 1 çıkarılan · 3 değişen · bilgi değişikliği" */
export function diffSummaryText(diff, t) {
  const c = diffCounts(diff);
  const parts = [];
  if (c.added) parts.push(`${c.added} ${t("lib_diff_added_word")}`);
  if (c.removed) parts.push(`${c.removed} ${t("lib_diff_removed_word")}`);
  if (c.changed) parts.push(`${c.changed} ${t("lib_diff_changed_word")}`);
  if (c.meta) parts.push(t("lib_diff_meta_word"));
  return parts.length ? parts.join(" · ") : t("lib_diff_none");
}

/** Çakışan her soruda varsayılan seçim: öneri. */
export function defaultChoices(diff) {
  const out = {};
  diff.changed.filter((c) => c.conflict).forEach((c) => {
    out[c.qid] = "proposal";
  });
  return out;
}

/** Karar isteğindeki seçimler: yalnız çakışanlar, verilmeyen "proposal". */
export function choicesPayload(diff, choices) {
  const out = {};
  diff.changed
    .filter((c) => c.conflict)
    .forEach((c) => {
      out[c.qid] = choices?.[c.qid] === "current" ? "current" : "proposal";
    });
  return out;
}

/** İki sorunun hangi kısımları farklı (sıra sabit). */
export function changedParts(a, b) {
  const parts = [];
  if (a.type !== b.type) parts.push("type");
  if (String(a.text || "").trim() !== String(b.text || "").trim()) parts.push("text");
  if (JSON.stringify(a.options ?? []) !== JSON.stringify(b.options ?? [])) parts.push("options");
  if (a.correctIndex !== b.correctIndex || a.correctBool !== b.correctBool) parts.push("correct");
  return parts;
}

export const PROPOSAL_STATUS_KEY = {
  open: "lib_prop_open",
  accepted: "lib_prop_accepted",
  rejected: "lib_prop_rejected",
  withdrawn: "lib_prop_withdrawn",
};

/** Karar gövdesi (/proposals/decide). */
export function decideBody({ proposalId, action, message, diff, choices }) {
  const body = { proposalId, action };
  const reply = replyPayload(message);
  if (reply) body.message = reply;
  if (action === "accept" && diff) {
    const picks = choicesPayload(diff, choices);
    if (Object.keys(picks).length) body.choices = picks;
  }
  return body;
}

/* ---------- Paket sihirbazı ---------- */

export const TAGS_MAX = 8;
export const TAG_MAX = 30;

export const PACKAGE_STEPS = ["info", "questions", "summary"];

/** "a, b ,a" → ["a", "b"] (büyük/küçük harf farkı tekrar sayılır). */
export function parseTags(text) {
  const seen = new Set();
  const out = [];
  for (const raw of String(text || "").split(",")) {
    const tag = raw.trim();
    const key = tag.toLocaleLowerCase("tr");
    if (!tag || seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
}

/** Paket bilgisinin ilk eksiği; yoksa null. */
export function packageInfoIssue({ title, description, tags }) {
  const t = String(title || "").trim();
  if (!t || t.length > LIMITS.titleMax) return "title";
  if (String(description || "").trim().length > LIMITS.descriptionMax) return "description";
  if ((tags || []).length > TAGS_MAX) return "tagsCount";
  if ((tags || []).some((tag) => tag.length > TAG_MAX)) return "tagLength";
  return null;
}

export const PACKAGE_ISSUE_KEY = {
  title: "lib_issue_title",
  description: "lib_issue_description",
  tagsCount: "lib_issue_tags_count",
  tagLength: "lib_issue_tag_length",
};

const infoOk = (f) =>
  packageInfoIssue({ title: f.title, description: f.description, tags: parseTags(f.tagsText) }) === null;
const questionsOk = (f) => f.questions.length > 0 && questionsIssue(f.questions) === null;

/** Adım tamam mı: bilgi kuralları, en az bir geçerli soru, özette hepsi. */
export function canAdvancePackage(step, f) {
  if (step === "info") return infoOk(f);
  if (step === "questions") return questionsOk(f);
  return infoOk(f) && questionsOk(f);
}

/** Düzenlenen paketten (ya da boş) sihirbaz formu. */
export function packageForm(existing) {
  return {
    title: existing?.title ?? "",
    description: existing?.description ?? "",
    tagsText: (existing?.tags ?? []).join(", "),
    questions: (existing?.questions ?? []).map((q) => (q.options ? { ...q, options: [...q.options] } : { ...q })),
  };
}

const sameContent = (a, b) =>
  a.type === b.type &&
  String(a.text || "").trim().toLocaleLowerCase("tr") === String(b.text || "").trim().toLocaleLowerCase("tr");

/**
 * Aramadan gelen soruyu listeye ekler: aynı tip ve metin varsa ya da liste
 * doluysa liste aynen döner. Eklenen soru yeni yerel kimlik alır.
 */
export function appendQuestion(list, q) {
  if (list.length >= LIMITS.questionsMax) return list;
  if (list.some((x) => sameContent(x, q))) return list;
  const copy = { qid: newLocalQid(), type: q.type, text: q.text };
  if (q.options) copy.options = [...q.options];
  if (typeof q.correctIndex === "number") copy.correctIndex = q.correctIndex;
  if (typeof q.correctBool === "boolean") copy.correctBool = q.correctBool;
  return [...list, copy];
}
